import { useMemo, useState } from "react";
import { Pin, Trash2, Pencil, Search, Plus, MessageSquare } from "lucide-react";
import { deleteAIConversation, pinAIConversation, renameAIConversation } from "../../services/aiApi";

function groupByDate(conversations) {
  const buckets = { Today: [], Yesterday: [], "Last 7 days": [], Older: [] };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const week = new Date(today); week.setDate(week.getDate() - 7);
  for (const c of conversations) {
    const t = new Date(c.updated_at);
    if (t >= today) buckets.Today.push(c);
    else if (t >= yest) buckets.Yesterday.push(c);
    else if (t >= week) buckets["Last 7 days"].push(c);
    else buckets.Older.push(c);
  }
  return buckets;
}

export default function ChatSidebar({ history, activeId, onOpen, onNew, onMutated }) {
  const [query, setQuery] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return history;
    return history.filter((h) =>
      (h.title || "").toLowerCase().includes(q) ||
      (h.last_message_preview || "").toLowerCase().includes(q)
    );
  }, [history, query]);

  const pinned = filtered.filter((h) => h.pinned);
  const unpinned = filtered.filter((h) => !h.pinned);
  const groups = groupByDate(unpinned);

  const startRename = (h) => { setRenameId(h.id); setRenameValue(h.title || ""); };
  const commitRename = async () => {
    if (renameId && renameValue.trim()) {
      try { await renameAIConversation(renameId, renameValue.trim()); } catch { /* ignore */ }
    }
    setRenameId(null);
    setRenameValue("");
    onMutated?.();
  };

  const togglePin = async (h) => { await pinAIConversation(h.id, !h.pinned); onMutated?.(); };
  const remove = async (h) => {
    if (!window.confirm("Delete this conversation?")) return;
    await deleteAIConversation(h.id);
    onMutated?.();
  };

  const renderItem = (h) => (
    <li
      key={h.id}
      className={`ai-side-item ${activeId === h.id ? "is-active" : ""}`}
      onClick={() => onOpen(h.id)}
    >
      <MessageSquare size={13} className="ai-side-item-ic" />
      {renameId === h.id ? (
        <input
          autoFocus
          className="ai-side-rename"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenameId(null); }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="ai-side-item-title" title={h.title}>{h.title}</span>
      )}
      <span className="ai-side-actions">
        <button type="button" title={h.pinned ? "Unpin" : "Pin"} onClick={(e) => { e.stopPropagation(); togglePin(h); }}>
          <Pin size={12} className={h.pinned ? "is-on" : ""} />
        </button>
        <button type="button" title="Rename" onClick={(e) => { e.stopPropagation(); startRename(h); }}>
          <Pencil size={12} />
        </button>
        <button type="button" title="Delete" onClick={(e) => { e.stopPropagation(); remove(h); }}>
          <Trash2 size={12} />
        </button>
      </span>
    </li>
  );

  return (
    <aside className="ai-sidebar">
      <button type="button" className="ai-side-new" onClick={onNew}>
        <Plus size={14} /> New chat
      </button>

      <div className="ai-side-search">
        <Search size={13} />
        <input
          placeholder="Search chats"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {pinned.length > 0 ? (
        <div className="ai-side-group">
          <div className="ai-side-group-title">Pinned</div>
          <ul>{pinned.map(renderItem)}</ul>
        </div>
      ) : null}

      {Object.entries(groups).map(([label, items]) =>
        items.length === 0 ? null : (
          <div key={label} className="ai-side-group">
            <div className="ai-side-group-title">{label}</div>
            <ul>{items.map(renderItem)}</ul>
          </div>
        )
      )}

      {history.length === 0 ? (
        <div className="ai-side-empty">No conversations yet.</div>
      ) : null}
    </aside>
  );
}
